import React, { useEffect, useRef, useState } from "react";
import { Landmark, RefreshCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectCollectionAccount,
  selectCollectionAccountStatus,
} from "../../features/collection/collection-slice";
import {
  fetchCollectionAccount,
  refreshCollectionAccount,
  startCollectionOnboarding,
} from "../../features/collection/slice-extensions/collection-slice-extension";
import { CollectionAccountStatus } from "../../features/collection/models/CollectionAccountStatus";
import { connectCountryOptions } from "../../features/collection/utils/connectCountries";
import { methodsLabel } from "../../features/collection/utils/methodLabel";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";

/**
 * The "Automatic collection" card on the billing overview: connect the organization's
 * Stripe account once (any Connect country, PD-0043), then track its readiness and the
 * methods members can authorize. Copy follows the locked wording — automatic collection
 * is built in, Stripe's fees apply.
 */
const CollectionAccountCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const account = useAppSelector(selectCollectionAccount);
  const loading = useAppSelector(selectCollectionAccountStatus);
  const [country, setCountry] = useState("DE");
  const [redirecting, setRedirecting] = useState(false);
  const refreshedRef = useRef(false);

  const connectParam = searchParams.get("connect");

  useEffect(() => {
    // Returning from Stripe onboarding: pull fresh flags instead of trusting
    // (possibly webhook-less local) state, and clean the URL.
    if (connectParam && !refreshedRef.current) {
      refreshedRef.current = true;
      dispatch(refreshCollectionAccount());
      searchParams.delete("connect");
      setSearchParams(searchParams, { replace: true });
      return;
    }
    dispatch(fetchCollectionAccount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleOnboarding = async () => {
    setRedirecting(true);
    try {
      const url = await dispatch(
        startCollectionOnboarding(account?.country ?? country)
      ).unwrap();
      window.location.assign(url);
    } catch {
      setRedirecting(false);
    }
  };

  if (!account) {
    return null;
  }

  const badge = (() => {
    switch (account.status) {
      case CollectionAccountStatus.Active:
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
      case CollectionAccountStatus.Restricted:
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300";
      default:
        return "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70";
    }
  })();

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-grey-10 dark:text-grey-90">
          <Landmark size={20} className="text-grey-30 dark:text-grey-70" />
          {t("collection.card.title")}
        </h3>
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${badge}`}>
          {t(`collection.accountStatus.${account.status}`, { defaultValue: account.status })}
        </span>
      </div>

      <div className="mt-3 space-y-3">
        <p className="text-sm text-grey-40 dark:text-grey-60">
          {account.status === CollectionAccountStatus.Active
            ? t("collection.card.activeHint")
            : t("collection.card.intro")}
        </p>

        {account.methods.length > 0 && (
          <p className="text-sm text-grey-20 dark:text-grey-90" data-testid="collection-methods">
            {t("collection.account.methods", { methods: methodsLabel(account.methods, t) })}
          </p>
        )}

        {account.status === CollectionAccountStatus.Restricted && account.disabledReason && (
          <p className="text-sm font-medium text-red-700 dark:text-red-400">
            {t("collection.card.restrictedReason", { reason: account.disabledReason })}
          </p>
        )}

        {account.status !== CollectionAccountStatus.Active && (
          <div className="flex flex-wrap items-center gap-2">
            {account.status === CollectionAccountStatus.NotConnected && (
              <select
                className="input w-auto"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                aria-label={t("collection.card.countryLabel")}
              >
                {connectCountryOptions(i18n.language).map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={redirecting || loading === LoadingStatus.Loading}
              onClick={handleOnboarding}
            >
              {account.status === CollectionAccountStatus.NotConnected
                ? t("collection.card.connect")
                : t("collection.card.resume")}
            </button>
            {account.status !== CollectionAccountStatus.NotConnected && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={loading === LoadingStatus.Loading}
                onClick={() => dispatch(refreshCollectionAccount())}
              >
                <RefreshCw size={16} />
                <span>{t("collection.card.refresh")}</span>
              </button>
            )}
          </div>
        )}

        <p className="text-xs text-grey-50 dark:text-grey-60">{t("collection.card.fees")}</p>
      </div>
    </div>
  );
};

export default CollectionAccountCard;
